import { supabase } from '../lib/supabase';

/**
 * Service for managing referring dentist contacts
 */
export const contactService = {
  /**
   * Get all contacts for a practitioner
   * @param {string} practitionerId - The practitioner's ID
   * @returns {Promise<Array>} Array of contact objects
   */
  async getContacts(practitionerId) {
    try {
      const { data, error } = await supabase?.from('referring_dentists_contacts')?.select('*')?.eq('practitioner_id', practitionerId)?.order('last_referral_date', { ascending: false });

      if (error) throw error;

      return data?.map(contact => ({
        id: contact?.id,
        practitionerId: contact?.practitioner_id,
        dentistName: contact?.dentist_name,
        practiceName: contact?.practice_name,
        email: contact?.email,
        phone: contact?.phone,
        firstReferralDate: contact?.first_referral_date,
        lastReferralDate: contact?.last_referral_date,
        totalReferrals: contact?.total_referrals,
        notes: contact?.notes,
        createdAt: contact?.created_at,
        updatedAt: contact?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching contacts:', error);
      throw error;
    }
  },

  /**
   * Search contacts by name, practice, email, or phone
   * @param {string} practitionerId - The practitioner's ID
   * @param {string} searchTerm - Search term
   * @returns {Promise<Array>} Filtered array of contact objects
   */
  async searchContacts(practitionerId, searchTerm) {
    try {
      if (!searchTerm) {
        return await this.getContacts(practitionerId);
      }

      const { data, error } = await supabase?.from('referring_dentists_contacts')?.select('*')?.eq('practitioner_id', practitionerId)?.or(`dentist_name.ilike.%${searchTerm}%,practice_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,phone.ilike.%${searchTerm}%`)?.order('last_referral_date', { ascending: false });

      if (error) throw error;

      return data?.map(contact => ({
        id: contact?.id,
        practitionerId: contact?.practitioner_id,
        dentistName: contact?.dentist_name,
        practiceName: contact?.practice_name,
        email: contact?.email,
        phone: contact?.phone,
        firstReferralDate: contact?.first_referral_date,
        lastReferralDate: contact?.last_referral_date,
        totalReferrals: contact?.total_referrals,
        notes: contact?.notes,
        createdAt: contact?.created_at,
        updatedAt: contact?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error searching contacts:', error);
      throw error;
    }
  },

  /**
   * Update contact notes
   * @param {string} contactId - Contact ID
   * @param {string} notes - Notes text
   * @returns {Promise<Object>} Updated contact
   */
  async updateContactNotes(contactId, notes) {
    try {
      const { data, error } = await supabase?.from('referring_dentists_contacts')?.update({ 
          notes,
          updated_at: new Date()?.toISOString()
        })?.eq('id', contactId)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        practitionerId: data?.practitioner_id,
        dentistName: data?.dentist_name,
        practiceName: data?.practice_name,
        email: data?.email,
        phone: data?.phone,
        firstReferralDate: data?.first_referral_date,
        lastReferralDate: data?.last_referral_date,
        totalReferrals: data?.total_referrals,
        notes: data?.notes,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating contact notes:', error);
      throw error;
    }
  },

  /**
   * Delete a contact
   * @param {string} contactId - Contact ID
   * @returns {Promise<void>}
   */
  async deleteContact(contactId) {
    try {
      const { error } = await supabase?.from('referring_dentists_contacts')?.delete()?.eq('id', contactId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting contact:', error);
      throw error;
    }
  },

  /**
   * Export contacts to CSV format
   * @param {Array} contacts - Array of contact objects
   * @returns {string} CSV string
   */
  exportToCSV(contacts) {
    if (!contacts || contacts?.length === 0) {
      return '';
    }

    const headers = [
      'Nom du Dentiste',
      'Cabinet',
      'Email',
      'Téléphone',
      'Première Référence',
      'Dernière Référence',
      'Total Références',
      'Notes'
    ];

    const rows = contacts?.map(contact => [
      contact?.dentistName || '',
      contact?.practiceName || '',
      contact?.email || '',
      contact?.phone || '',
      contact?.firstReferralDate ? new Date(contact.firstReferralDate)?.toLocaleDateString('fr-FR') : '',
      contact?.lastReferralDate ? new Date(contact.lastReferralDate)?.toLocaleDateString('fr-FR') : '',
      contact?.totalReferrals || 0,
      contact?.notes || ''
    ]);

    const csvContent = [
      headers?.join(','),
      ...rows?.map(row => row?.map(cell => `"${cell}"`)?.join(','))
    ]?.join('\n');

    return csvContent;
  },

  /**
   * Download contacts as CSV file
   * @param {Array} contacts - Array of contact objects
   * @param {string} filename - Filename for download
   */
  downloadCSV(contacts, filename = 'contacts_dentistes.csv') {
    const csvContent = this.exportToCSV(contacts);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link?.setAttribute('href', url);
    link?.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  }
};